import axios, { AxiosError } from 'axios';
import { apiRoute } from '../constants/api-constants';
import { StaysApiError } from './stays-client';

/**
 * Independent Stays plan IS9: a hotel's room types and its reception.
 *   room types — BE src/stays/routes/room-types.js (stays:read / stays:promote)
 *   reception  — BE src/stays/routes/reception.js (stays:desk reads, stays:reception writes)
 * Every reception answer is no-store and on the PROPERTY's clock (tz fields).
 */

const wrap = async <T>(p: Promise<{ data: T }>, fallback: string): Promise<T> => {
  try {
    const { data } = await p;

    return data;
  } catch (e) {
    const err = e as AxiosError<Record<string, unknown>>;
    if (err && err.response) throw new StaysApiError(err.response.status, err.response.data || null, fallback);
    throw new StaysApiError(0, null, fallback);
  }
};
const enc = (v: string | number) => encodeURIComponent(String(v));

// ── room types ──────────────────────────────────────────────────────────────
export interface HotelRoom {
  listing_id: string;
  unit_id: number;
  label: string;
  status: 'draft' | 'active' | 'paused' | 'retired';
  version: number;
}

export interface RoomType {
  id: string;
  building_id: number;
  name: string;
  max_guests: number;
  check_in_time: string;
  check_out_time: string;
  sort: number;
  version: number;
  rooms: HotelRoom[];
}

export interface RoomTypeInput {
  name?: string;
  max_guests?: number;
  check_in_time?: string;
  check_out_time?: string;
  sort?: number;
}

export const listRoomTypes = (buildingId: number): Promise<{ building_id: number; items: RoomType[]; rooms_max: number; rooms_total: number }> =>
  wrap(axios.get(`${apiRoute}/stays/buildings/${enc(buildingId)}/room-types`), 'Could not load the room types.');

export const createRoomType = (
  buildingId: number,
  input: RoomTypeInput & { name: string; rooms?: { label: string }[] }
): Promise<RoomType> =>
  wrap(axios.post(`${apiRoute}/stays/buildings/${enc(buildingId)}/room-types`, input), 'Could not add the room type.');

export const editRoomType = (id: string, input: RoomTypeInput & { version: number }): Promise<RoomType> =>
  wrap(axios.patch(`${apiRoute}/stays/room-types/${enc(id)}`, input), 'Could not save the room type.');

export const archiveRoomType = (id: string, version: number): Promise<void> =>
  wrap(axios.post(`${apiRoute}/stays/room-types/${enc(id)}/archive`, { version }), 'Could not remove the room type.').then(
    () => undefined
  );

export const addRooms = (id: string, rooms: { label: string }[]): Promise<RoomType> =>
  wrap(axios.post(`${apiRoute}/stays/room-types/${enc(id)}/rooms`, { rooms }), 'Could not add the rooms.');

// ── reception ───────────────────────────────────────────────────────────────
export type IdState = 'not_given' | 'read' | 'needs_look' | 'checked' | 'rescan_asked';
export type StepState = 'todo' | 'done' | 'not_required' | string;

export interface ReceptionGuest {
  guest_id: string;
  lead: boolean;
  first_name: string;
  last_name: string;
  id_state: IdState;
  id_method: 'ocr' | 'manual' | null;
  can_check: boolean;
}

export interface ReceptionStay {
  reservation_id: string;
  version: number;
  status: 'confirmed' | 'checked_in' | 'checked_out' | 'cancelled' | 'no_show';
  first_name: string;
  last_name: string;
  party_size: number;
  adults: number | null;
  check_in: string;
  check_out: string;
  nights: number;
  room_type_id: string | null;
  room_type_name: string | null;
  room_assigned: boolean;
  room: { listing_id: string; label: string } | null;
  expected_arrival_time: string | null;
  check_in_time: string;
  check_out_time: string;
  checkin: {
    trip: StepState;
    identity: StepState;
    rules: StepState;
    complete: boolean;
    link_status: string | null;
    link_sent: boolean;
    link_opened: boolean;
  };
  guests: ReceptionGuest[];
  ids_to_look_at: number;
  checked_in_at: string | null;
  self_arrived_at: string | null;
  help_open: number;
  access_revoked: boolean;
  arrives_today: boolean;
  leaves_today: boolean;
}

export interface ReceptionRoomType {
  id: string;
  name: string;
  rooms: number;
  taken: number;
  waiting: number;
  free: number;
}

export interface ReceptionBoard {
  building_id: number;
  name: string;
  day: string;
  today: string;
  tz?: string | null;
  tz_source?: string | null;
  arrivals: ReceptionStay[];
  in_house: ReceptionStay[];
  departures: ReceptionStay[];
  room_types: ReceptionRoomType[];
  counts: { arrivals: number; arrivals_ready: number; need_room: number; in_house: number; departures: number };
}

export interface ReceptionHotel {
  building_id: number;
  name: string;
  zone: string;
  rooms: number;
  today: string;
}

export interface FreeRooms {
  room_type_id: string;
  room_type_name: string;
  from: string;
  to: string;
  rooms: { listing_id: string; label: string; paused: boolean }[];
}

export interface GuestLink {
  url: string;
  qr_svg: string | null;
}

const rec = `${apiRoute}/stays/reception`;

export const receptionHotels = (): Promise<{ items: ReceptionHotel[] }> =>
  wrap(axios.get(`${rec}/hotels`), 'Could not load your hotels.');

export const receptionBoard = (buildingId: number, day?: string): Promise<ReceptionBoard> =>
  wrap(
    axios.get(`${rec}/buildings/${enc(buildingId)}/board`, { params: day ? { day } : {} }),
    'Could not load reception.'
  );

export const freeRoomsFor = (reservationId: string): Promise<FreeRooms> =>
  wrap(axios.get(`${rec}/free-rooms`, { params: { reservation_id: reservationId } }), 'Could not load the free rooms.');

export const freeRoomsOfType = (roomTypeId: string, from: string, to: string): Promise<FreeRooms> =>
  wrap(axios.get(`${rec}/free-rooms`, { params: { room_type_id: roomTypeId, from, to } }), 'Could not load the free rooms.');

export const giveRoom = (reservationId: string, listingId: string, version: number): Promise<ReceptionStay> =>
  wrap(axios.post(`${rec}/reservations/${enc(reservationId)}/room`, { listing_id: listingId, version }), 'Could not give the room.');

export const markCheckedIn = (reservationId: string, version: number): Promise<ReceptionStay> =>
  wrap(axios.post(`${rec}/reservations/${enc(reservationId)}/checked-in`, { version }), 'Could not check the guest in.');

export const markCheckedOut = (reservationId: string, version: number): Promise<ReceptionStay> =>
  wrap(axios.post(`${rec}/reservations/${enc(reservationId)}/checked-out`, { version }), 'Could not check the guest out.');

export const showGuestLink = (reservationId: string): Promise<GuestLink & { rotated: boolean }> =>
  wrap(axios.post(`${rec}/reservations/${enc(reservationId)}/link`, {}), 'Could not show the check-in link.');

export const markIdChecked = (guestId: string): Promise<ReceptionStay> =>
  wrap(axios.post(`${rec}/guests/${enc(guestId)}/id-checked`, {}), 'Could not mark the ID as checked.');

export const askRescan = (guestId: string): Promise<ReceptionStay> =>
  wrap(axios.post(`${rec}/guests/${enc(guestId)}/rescan`, {}), 'Could not ask for a new scan.');

export interface WalkInInput {
  building_id: number;
  room_type_id: string;
  nights: number;
  party_size: number;
  lead_first_name: string;
  lead_last_name: string;
  lead_email?: string;
  listing_id?: string;
  send_email?: boolean;
}

export interface WalkInCreated {
  reservation: ReceptionStay;
  link: (GuestLink & { shown_once: boolean }) | null;
  email: string;
}

export const createWalkIn = (input: WalkInInput): Promise<WalkInCreated> =>
  wrap(axios.post(`${rec}/walk-ins`, input), 'Could not add the walk-in.');
